import React, { createContext, useContext } from 'react';
import { ReactKeycloakProvider, useKeycloak as useKeycloakHook } from '@react-keycloak/web';
import keycloak, { isKeycloakConfigured } from '../keycloak';

interface User {
  id: string;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

interface KeycloakAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  token: string | null;
  login: () => void;
  logout: () => void;
  register: () => void;
}

const KeycloakAuthContext = createContext<KeycloakAuthContextType | undefined>(undefined);

export const useKeycloakAuth = () => {
  const context = useContext(KeycloakAuthContext);
  if (context === undefined) {
    throw new Error('useKeycloakAuth must be used within a KeycloakAuthProvider');
  }
  return context;
};

interface KeycloakAuthInnerProviderProps {
  children: React.ReactNode;
}

// Inner provider that uses Keycloak hook
const KeycloakAuthInnerProvider: React.FC<KeycloakAuthInnerProviderProps> = ({ children }) => {
  const { keycloak: kc, initialized } = useKeycloakHook();

  // Auto-refresh token before it expires
  React.useEffect(() => {
    if (!kc.authenticated) return;

    // Update token every minute (check if it needs refresh)
    const interval = setInterval(() => {
      kc.updateToken(70) // Refresh if token expires in less than 70 seconds
        .then((refreshed) => {
          if (refreshed) {
            console.log('Token refreshed successfully');
          }
        })
        .catch((error) => {
          console.error('Failed to refresh token:', error);
          // Token refresh failed, logout user
          kc.logout();
        });
    }, 60000); // Check every 60 seconds

    return () => clearInterval(interval);
  }, [kc, kc.authenticated]);

  // Extract user info from Keycloak token
  const user = kc.authenticated && kc.tokenParsed
    ? {
        id: kc.tokenParsed.sub || '',
        username: kc.tokenParsed.preferred_username || kc.tokenParsed.email || '',
        email: kc.tokenParsed.email || '',
        first_name: kc.tokenParsed.given_name || '',
        last_name: kc.tokenParsed.family_name || '',
      }
    : null;

  const login = () => {
    kc.login();
  };

  const logout = () => {
    // Clear localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    // Keycloak logout
    kc.logout();
  };

  const register = () => {
    kc.register();
  };

  const value: KeycloakAuthContextType = {
    isAuthenticated: kc.authenticated || false,
    isLoading: !initialized,
    user,
    token: kc.token || null,
    login,
    logout,
    register,
  };

  return (
    <KeycloakAuthContext.Provider value={value}>
      {children}
    </KeycloakAuthContext.Provider>
  );
};

interface KeycloakAuthProviderProps {
  children: React.ReactNode;
}

// Main provider that wraps ReactKeycloakProvider
export const KeycloakAuthProvider: React.FC<KeycloakAuthProviderProps> = ({ children }) => {
  // Check if Keycloak is configured
  if (!isKeycloakConfigured()) {
    console.warn('Keycloak is not configured. Falling back to legacy authentication.');
    // Return a minimal context that indicates Keycloak is not available
    const fallbackValue: KeycloakAuthContextType = {
      isAuthenticated: false,
      isLoading: false,
      user: null,
      token: null,
      login: () => console.error('Keycloak not configured'),
      logout: () => console.error('Keycloak not configured'),
      register: () => console.error('Keycloak not configured'),
    };

    return (
      <KeycloakAuthContext.Provider value={fallbackValue}>
        {children}
      </KeycloakAuthContext.Provider>
    );
  }

  // Keycloak initialization config
  const keycloakInitConfig = {
    onLoad: 'check-sso' as const, // Automatically check for existing SSO session
    pkceMethod: 'S256' as const,
    checkLoginIframe: false, // Disable iframe-based SSO check
    silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
    // Enable automatic token refresh
    // Token will be refreshed if it expires within 70 seconds
    enableLogging: process.env.NODE_ENV === 'development',
  };

  const onKeycloakEvent = (event: string, error?: any) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Keycloak event:', event, error);
    }
  };

  const onKeycloakTokens = (tokens: any) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Keycloak tokens updated:', {
        token: tokens.token?.substring(0, 20) + '...',
        refreshToken: tokens.refreshToken ? 'present' : 'absent',
      });
    }
  };

  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      initOptions={keycloakInitConfig}
      onEvent={onKeycloakEvent}
      onTokens={onKeycloakTokens}
    >
      <KeycloakAuthInnerProvider>
        {children}
      </KeycloakAuthInnerProvider>
    </ReactKeycloakProvider>
  );
};

// Hook to check if Keycloak is available
export const useKeycloakAvailable = () => {
  return isKeycloakConfigured();
};
